// WEBDOC APNA Clinic (PBX 4455) sync API client.
// Dev: /api is proxied to http://localhost:9000 (see src/setupProxy.js).
// Live: set REACT_APP_APNA_CLINIC_4455_URL (origin only, no trailing /api).

const PATH = "/api/webdoc-apna-clinic-4455";

function apiRoot() {
  const origin = String(process.env.REACT_APP_APNA_CLINIC_4455_URL || "").replace(
    /\/$/,
    ""
  );
  return `${origin}${PATH}`;
}

function toQuery(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : "";
}

function explainNonJsonError(text, status) {
  const raw = String(text || "").trim();
  if (/Cannot GET \/api\/webdoc-apna-clinic-4455/i.test(raw)) {
    return [
      "PBX 4455 API is not on this server (Express 404).",
      "Start the WEBDOC APNA Clinic 4455 Express API on http://localhost:9000,",
      "then restart this app with npm start so the /api proxy loads.",
      "Do not point port 9000 at the Dialer Recording API.",
    ].join(" ");
  }
  if (raw.startsWith("<!DOCTYPE") || raw.startsWith("<html")) {
    return `API returned HTML instead of JSON (HTTP ${status}). Check that the 4455 backend is running on port 9000.`;
  }
  return raw || `HTTP ${status}`;
}

async function request(path, options = {}) {
  let res;
  try {
    res = await fetch(`${apiRoot()}${path}`, {
      ...options,
      headers: {
        Accept: "application/json",
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...(options.headers || {}),
      },
    });
  } catch (networkErr) {
    const err = new Error(
      "Could not reach the PBX 4455 API. Confirm it is running on http://localhost:9000 and that the /api proxy is active."
    );
    err.connectionFailure = true;
    err.cause = networkErr;
    throw err;
  }

  let body = null;
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    body = await res.json();
  } else {
    const text = await res.text();
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = { success: false, message: explainNonJsonError(text, res.status) };
    }
  }

  if (!res.ok) {
    const err = new Error(body?.message || body?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }

  if (body && body.success === false) {
    const err = new Error(formatFailureMessage(body));
    err.body = body;
    throw err;
  }

  return body;
}

function formatFailureMessage(body) {
  const main = body?.message || body?.error || "Request failed";
  if (!body || typeof body !== "object") return main;
  const extra = [];
  for (const [key, val] of Object.entries(body)) {
    if (["success", "message", "error"].includes(key)) continue;
    if (/pass|secret|pwd|credential|user|host|server/i.test(key)) continue;
    if (val && typeof val === "object") {
      const ok = val.ok ?? val.success ?? val.connected;
      const msg = val.message || val.error || val.detail;
      if (ok === false || msg) {
        extra.push(`${key}: ${ok === false ? "failed" : "ok"}${msg ? ` (${msg})` : ""}`);
      }
    }
  }
  return extra.length ? `${main} — ${extra.join("; ")}` : main;
}

export function formatApiError(err) {
  if (!err) return "Request failed";
  if (err.body) return formatFailureMessage(err.body) || err.message;
  return err.message || "Request failed";
}

export function getConfig() {
  return request("/config");
}

export function putConfig(payload) {
  return request("/config", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function testConnections() {
  return request("/test-connections", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function runNow(dryRun) {
  return request("/run-now", {
    method: "POST",
    body: JSON.stringify({ dry_run: Boolean(dryRun) }),
  });
}

export function getRuns({ page = 1, limit = 20 } = {}) {
  return request(`/runs${toQuery({ page, limit })}`);
}

export function getRunById(id) {
  return request(`/runs/${encodeURIComponent(id)}`);
}

export function getRunItems(id, { page = 1, limit = 100, outcome } = {}) {
  return request(
    `/runs/${encodeURIComponent(id)}/items${toQuery({ page, limit, outcome })}`
  );
}
